import { Box, Typography, useTheme } from '@mui/material';
import { Icon } from '@phosphor-icons/react';
import React, { createContext, useContext } from 'react';
import { useDarkMode } from '../../../hooks/useDarkMode';
import { IconButton } from './IconButton';
import { CustomColorPalette } from './types';

interface ExtendedSidebarContextValue {
  isOpen: boolean;
  isDarkMode: boolean;
  palette: CustomColorPalette;
  isSearchOpen: boolean;
  setIsSearchOpen: React.Dispatch<React.SetStateAction<boolean>>;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
}

const ExtendedSidebarContext =
  createContext<ExtendedSidebarContextValue | null>(null);

export const useExtendedSidebarContext = () => {
  const context = useContext(ExtendedSidebarContext);
  if (!context) {
    throw new Error(
      'ExtendedSidebar components must be used within ExtendedSidebar.Root',
    );
  }
  return context;
};

interface ExtendedSidebarRootProps {
  isOpen: boolean;
  children: React.ReactNode;
}

const ExtendedSidebarRoot: React.FC<ExtendedSidebarRootProps> = ({
  isOpen,
  children,
}) => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');

  const value = React.useMemo(
    () => ({
      isOpen,
      isDarkMode,
      palette,
      isSearchOpen,
      setIsSearchOpen,
      searchTerm,
      setSearchTerm,
    }),
    [isOpen, isDarkMode, palette, isSearchOpen, searchTerm],
  );

  return (
    <ExtendedSidebarContext.Provider value={value}>
      <Box
        sx={{
          width: isOpen ? '257px' : '0px',
          maxWidth: '257px',
          borderRight: `1px solid ${
            isDarkMode ? palette.gray[700] : palette.gray[300]
          }`,
          paddingX: isOpen ? '16px' : '0px',
          paddingTop: isSearchOpen ? '10px' : '16px',
          paddingBottom: '12px',
          backgroundColor: isDarkMode ? palette.gray[900] : palette.gray.A100,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'width 0.3s ease, padding 0.3s ease',
        }}
      >
        {children}
      </Box>
    </ExtendedSidebarContext.Provider>
  );
};

interface ExtendedSidebarHeaderProps {
  children: React.ReactNode;
}

const ExtendedSidebarHeader: React.FC<ExtendedSidebarHeaderProps> = ({
  children,
}) => {
  const { isOpen } = useExtendedSidebarContext();

  return (
    <Box
      sx={{
        padding: '16px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        opacity: isOpen ? 1 : 0,
        transition: 'opacity 0.3s ease',
        pointerEvents: isOpen ? 'auto' : 'none',
      }}
    >
      {children}
    </Box>
  );
};

interface ExtendedSidebarTitleProps {
  children: React.ReactNode;
}

const ExtendedSidebarTitle: React.FC<ExtendedSidebarTitleProps> = ({
  children,
}) => {
  const { isDarkMode, palette, isSearchOpen } = useExtendedSidebarContext();

  return (
    <Typography
      sx={{
        color: isDarkMode ? palette.gray.A100 : palette.neutral[900],
        fontFeatureSettings: "'liga' off, 'clig' off",
        fontFamily: 'Roboto',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '166%',
        letterSpacing: '0.4px',
        display: isSearchOpen ? 'none' : 'block',
      }}
    >
      {children}
    </Typography>
  );
};

interface ExtendedSidebarActionsProps {
  children: React.ReactNode;
}

const ExtendedSidebarActions: React.FC<ExtendedSidebarActionsProps> = ({
  children,
}) => {
  const { isSearchOpen } = useExtendedSidebarContext();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        justifyContent: 'flex-end',
        ...(isSearchOpen && {
          '& > :not(.extended-sidebar-search)': {
            display: 'none',
          },
        }),
      }}
    >
      {children}
    </Box>
  );
};

interface ExtendedSidebarActionButtonProps {
  icon: Icon;
  onClick?: () => void;
  size?: number;
  color?: string;
}

const ExtendedSidebarActionButton: React.FC<ExtendedSidebarActionButtonProps> =
  ({ icon, onClick, size = 16, color }) => {
    return (
      <IconButton icon={icon} onClick={onClick} size={size} color={color} />
    );
  };

interface ExtendedSidebarBodyProps {
  children: React.ReactNode;
}

const ExtendedSidebarBody: React.FC<ExtendedSidebarBodyProps> = ({
  children,
}) => {
  const { isOpen } = useExtendedSidebarContext();

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        opacity: isOpen ? 1 : 0,
        transition: 'opacity 0.3s ease',
        pointerEvents: isOpen ? 'auto' : 'none',
      }}
    >
      {children}
    </Box>
  );
};

export const ExtendedSidebar = {
  Root: ExtendedSidebarRoot,
  Header: ExtendedSidebarHeader,
  Title: ExtendedSidebarTitle,
  Actions: ExtendedSidebarActions,
  ActionButton: ExtendedSidebarActionButton,
  Body: ExtendedSidebarBody,
};
